import type { TokenProvider } from './auth';
import { jwtExpiryMs } from './auth';

/** The token fields of a `/login` or `/refresh` response. */
export interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  /** Access-token lifetime in seconds. */
  expires_in: number;
  refresh_expires_in?: number;
  token_type?: string;
}

/** Minimal key/value store (sessionStorage, localStorage, or in-memory for embedded panels). */
export interface KeyValueStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

export function memoryStore(): KeyValueStore {
  const m = new Map<string, string>();
  return { get: (k) => m.get(k) ?? null, set: (k, v) => void m.set(k, v), remove: (k) => void m.delete(k) };
}

export function webStore(storage: Storage | undefined): KeyValueStore {
  if (!storage) return memoryStore();
  return {
    get: (k) => {
      try {
        return storage.getItem(k);
      } catch {
        return null;
      }
    },
    set: (k, v) => {
      try {
        storage.setItem(k, v);
      } catch {
        /* quota / private mode */
      }
    },
    remove: (k) => {
      try {
        storage.removeItem(k);
      } catch {
        /* ignore */
      }
    },
  };
}

export interface TokenSessionOptions {
  /** API base, e.g. `/web/v1`; the session POSTs `<baseUrl>/refresh`. */
  baseUrl: string;
  store: KeyValueStore;
  /** Key prefix, default `nb`. Keys: `<p>:token`, `<p>:refresh`, `<p>:token_exp`, `<p>:token_ttl`. */
  prefix?: string;
  fetch?: typeof fetch;
  /** Called when the refresh token is rejected or missing while a token was expected. */
  onExpired?: (reason: 'unauthorized' | 'refresh-failed' | 'server') => void;
  /** Refresh when this fraction of the access-token lifetime has passed (default 0.6). */
  refreshAt?: number;
  now?: () => number;
}

export interface TokenSession extends TokenProvider {
  /** Refresh now (one in-flight call shared by all callers); the new access token or null. */
  refresh(): Promise<string | null>;
  /** Store a fresh token pair (login/refresh response). */
  setTokens(tokens: TokenResponse): void;
  /** Access token as stored, without refreshing. */
  peek(): string | null;
  refreshToken(): string | null;
  isSignedIn(): boolean;
  /** Milliseconds until the scheduled refresh (<= 0 = now). */
  msUntilRefresh(): number;
  clear(): void;
  /** Start the background refresh timer (60 % of lifetime); returns the stop function. */
  startAutoRefresh(): () => void;
}

/** Access + refresh token pair kept in a store, refreshed at 60 % of its lifetime and on demand. */
export function createTokenSession(options: TokenSessionOptions): TokenSession {
  const p = options.prefix ?? 'nb';
  const store = options.store;
  const fetchImpl: typeof fetch = options.fetch ?? ((...args) => globalThis.fetch(...args));
  const refreshAt = options.refreshAt ?? 0.6;
  const now = options.now ?? (() => Date.now());
  const K = { token: `${p}:token`, refresh: `${p}:refresh`, exp: `${p}:token_exp`, ttl: `${p}:token_ttl` };
  let inflight: Promise<string | null> | null = null;

  const expMs = () => Number(store.get(K.exp)) || 0;
  const ttlMs = () => (Number(store.get(K.ttl)) || 900) * 1000;
  const refreshDue = () => expMs() - ttlMs() * (1 - refreshAt);

  function setTokens(t: TokenResponse) {
    store.set(K.token, t.access_token);
    if (t.refresh_token) store.set(K.refresh, t.refresh_token);
    const claimExp = jwtExpiryMs(t.access_token);
    const exp = claimExp || now() + (t.expires_in || 900) * 1000;
    store.set(K.exp, String(exp));
    store.set(K.ttl, String(t.expires_in || Math.max(60, Math.round((exp - now()) / 1000))));
  }

  function clear() {
    for (const k of Object.values(K)) store.remove(k);
  }

  async function refresh(): Promise<string | null> {
    if (inflight) return inflight;
    const rt = store.get(K.refresh);
    if (!rt) {
      options.onExpired?.('unauthorized');
      return null;
    }
    inflight = (async () => {
      try {
        const r = await fetchImpl(`${options.baseUrl.replace(/\/+$/, '')}/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: rt }),
        });
        const d = (await r.json().catch(() => ({}))) as Partial<TokenResponse>;
        if (r.ok && d.access_token) {
          setTokens({
            access_token: d.access_token,
            refresh_token: d.refresh_token || rt,
            expires_in: d.expires_in || 900,
          });
          return d.access_token;
        }
        if (r.status === 401 || r.status === 400 || r.status === 403) {
          clear();
          options.onExpired?.('refresh-failed');
        }
        return null;
      } catch {
        return null; // network: keep the old token, try again later
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  }

  async function getToken(): Promise<string | null> {
    const token = store.get(K.token);
    if (!token) return null;
    const t = now();
    if (t >= expMs() - 30_000) {
      // (Nearly) expired: only a refreshed token is usable.
      return (await refresh()) ?? (t < expMs() ? token : null);
    }
    if (t >= refreshDue()) {
      const fresh = await refresh();
      return fresh ?? token;
    }
    return token;
  }

  return {
    getToken,
    refresh,
    onAuthExpired: (reason) => {
      clear();
      options.onExpired?.(reason);
    },
    setTokens,
    peek: () => store.get(K.token),
    refreshToken: () => store.get(K.refresh),
    isSignedIn: () => !!store.get(K.token),
    msUntilRefresh: () => refreshDue() - now(),
    clear,
    startAutoRefresh() {
      let timer: ReturnType<typeof setTimeout> | undefined;
      let stopped = false;
      const schedule = () => {
        if (stopped || !store.get(K.token)) return;
        const wait = Math.max(5_000, refreshDue() - now());
        timer = setTimeout(async () => {
          await refresh();
          schedule();
        }, wait);
      };
      schedule();
      return () => {
        stopped = true;
        if (timer) clearTimeout(timer);
      };
    },
  };
}
